import React, { useState, useCallback, useEffect, useRef, useMemo } from "react";
import "../../css/salesorder.css";
import { GridLayout } from "../../../common/GridLayout";
import data from "../../../assets/apiRoutes.json";
import { SelectBox } from 'devextreme-react';
import { createStore } from 'devextreme-aspnet-data-nojquery';
import DataSource from "devextreme/data/data_source";
import { discardPendingEdits } from "../../../common/dataStore";
import RenderSelectBoxCell from "../../../common/RenderCell";
import {
    Column,
    DataGrid,
    Editing,
    Lookup,
    Toolbar,
    Item,
    Export,
    ColumnChooser,
    ColumnChooserSearch,
    StateStoring,
    RequiredRule,
} from "devextreme-react/data-grid";
import GridLayoutManager from "../../../gridLayoutCustomization/GridLayoutManager";

const EMPTY_GUID = "00000000-0000-0000-0000-000000000000";

// Difference-handling options, matching Delphi's grpActionSelection ItemIndex (uOMD0022).
export const DIFFERENCE_SAME_ITEM_NEW_LOT = 1;   // "Add Same Item With Different Lot"
export const DIFFERENCE_OTHER_ITEM_LOT = 2;      // "Add Different Item/Lot"
export const DIFFERENCE_DO_NOTHING = 3;          // "Do Nothing / Other"

const SearchResultsForDialog = (props) => {
    const dataGrid = useRef(null);
    var accessToken = props.accessToken;
    const gridKey = "10A011A1-E91F-4360-8B53-A59B2F357A1E";
    const [currentLayout, setCurrentLayout] = useState("Default");
    const saveState = useCallback((state) => { GridLayout.sendStorageRequest(gridKey, "POST", accessToken, state, currentLayout); }, [gridKey, currentLayout]);
    const loadState = useCallback(() => { return GridLayout.sendStorageRequest(gridKey, "GET", accessToken, null, currentLayout); }, [currentLayout]);

    const detailRowId = props.data?.detailRowId;
    const headerRowId = props.data?.headerRowId ?? props.headerRowId;

    // Delphi mode: fmDifference when the caller handed us a detail row to resolve, fmAdd otherwise.
    const isDifferenceMode = !!detailRowId;
    const priority = props.priorityEntities?.id;

    // Option 3 ("Do Nothing / Other") disables the grid outright, exactly as EnableDetailEdit(false) did.
    const editingDisabled = isDifferenceMode && priority === DIFFERENCE_DO_NOTHING;

    // Option 1 keeps the difference row's own item and warehouse - the user is only choosing a different
    // lot for the same item, so those two cells stay read-only (Delphi sets P_Item := FItemRowID itself).
    const itemLocked = isDifferenceMode && priority === DIFFERENCE_SAME_ITEM_NEW_LOT;

    const recordTypes = [
        { ID: "I", Text: "Inventory" },
        { ID: "N", Text: "Non Inventory" }
    ];

    // Until one of these is known there is nothing to scope the query to, and an unscoped load would
    // pull every transaction detail row in the company.
    const hasScope = !!(detailRowId || headerRowId);

    const detailDataSource = useMemo(() => {
        // An empty array, not null: dxDataGrid raises E1052 when dataSource is null, which showed as a
        // red banner on the first render and cleared itself once the props settled. That is only half
        // of E1052 though - the option being set is not the same as the data source being live; see
        // the wait in startRowForOption.
        if (!hasScope) return [];

        return new DataSource({
            // Plain createStore, not createConcurrencyAwareStore. Every row this grid holds was inserted by
            // this dialog seconds earlier and is visible to nobody else yet, so there is no version for a
            // second user to have moved on - and the conflict helper is actively wrong here: its recovery is
            // grid.refresh(), which re-runs the empty-key load below and would wipe the session's rows off
            // the screen. Concurrency on the line being resolved is enforced where it exists, on the listing
            // grid behind this dialog (AllocateAvailableQuantities.js) and server-side in
            // ProcessingService.EnsureDetailNotStale.
            store: createStore({
                key: "detailRowId",
                loadUrl: `${window._env_.REACT_APP_APIS}/${data.OM.SearchTransactionsDetails}`,
                insertUrl: `${window._env_.REACT_APP_APIS}/${data.OM.AddTransactionDetail}`,
                updateUrl: `${window._env_.REACT_APP_APIS}/${data.OM.EditTransactionDetail}`,
                // Delphi's navTransactionDetail carries abDelete (VisibleButtons in the OMD0022 DFM), and it
                // is the only way back from a line added here by mistake - see the Abort button, which
                // Delphi disables precisely because deleting is this grid's job.
                deleteUrl: `${window._env_.REACT_APP_APIS}/${data.OM.DeleteTransactionDetail}`,
                loadParams: {
                    // Delphi appends "AND ProcessFlag = 'Y'" to this grid's query in
                    // doOMM0000ModuleCreate, for BOTH modes - so it only ever shows lines staged in the
                    // current session. It never shows the transaction's existing detail lines, and never
                    // the line whose difference is being resolved: that one is in the statistics strip
                    // above. The grid therefore opens empty, and in difference mode the auto-started row
                    // is the only row.
                    //
                    // There is no staging flag on the web, so there is nothing to select on. Loading the
                    // empty key returns no rows, which is the same starting state; lines added here are
                    // patched in from each insert's own response.
                    Rowid: EMPTY_GUID
                },
                // Auth headers only. Rule 15: nothing here rebuilds the body or forces a Content-Type - the
                // store keeps speaking DevExtreme's standard form-encoded protocol, and the key and parent
                // FK are stamped by onRowInserting / onRowUpdating below.
                onBeforeSend: (method, ajaxOptions) => {
                    ajaxOptions.headers = {
                        ...(ajaxOptions.headers || {}),
                        Authorization: `Bearer ${accessToken}`,
                        access_token: accessToken
                    };
                }
            })
        });
    }, [hasScope, detailRowId, headerRowId, accessToken]);

    // How many lines this dialog session added, so the caller can run Delphi's exit guard
    // ("Difference Handling Is Set To ..., But There Are No Detail Records"). Delphi counted the
    // ProcessFlag='Y' rows staged in the session; with no staging on the web, rows added here is the
    // equivalent measure of "the user said they would add something and did".
    const sessionRowCount = useRef(0);

    useEffect(() => {
        // Reset whenever the dialog is pointed at a different transaction or line.
        sessionRowCount.current = 0;
        props.onSessionRowCountChange(0);
    }, [detailRowId, headerRowId]);

    const handleRowInserted = useCallback((e) => {
        sessionRowCount.current += 1;
        props.onSessionRowCountChange(sessionRowCount.current);
    }, [props.onSessionRowCountChange]);

    // Deleting a line the user just added puts the dialog back where it started, so the exit guard and the
    // Abort button both have to see it go. Never below zero: the grid only ever holds this session's rows.
    const handleRowRemoved = useCallback((e) => {
        sessionRowCount.current = Math.max(0, sessionRowCount.current - 1);
        props.onSessionRowCountChange(sessionRowCount.current);
    }, [props.onSessionRowCountChange]);

    // Rule 15: stamp the row's identity on the grid, never in onBeforeSend.
    const handleRowInserting = useCallback((e) => {
        e.data = {
            ...e.data,
            detailRowId: EMPTY_GUID,
            headerRowId: headerRowId,
            // Option 1 pins the item/warehouse the difference was raised against; the user only picks a lot.
            ...(itemLocked
                ? { itemid: props.data?.itemid, r_Warehouse: props.data?.r_Warehouse }
                : {})
        };
    }, [headerRowId, itemLocked, props.data]);

    const handleRowUpdating = useCallback((e) => {
        // Merge the whole previous row rather than a field whitelist (rule 15) - a cell edit otherwise sends
        // only the cell, and the server needs headerRowId and the rest of the line to price it. It also
        // carries concurrencyToken, which the server still checks even though this grid no longer raises the
        // conflict dialog for it.
        e.newData = { ...e.oldData, ...e.newData };
    }, []);

    /**
     * Defaults for an auto-started row, from Delphi's grpActionSelectionClick:
     * `QuantityOrdered := DiffQty` for both Add options, plus `P_Item := FItemRowID` for option 1.
     *
     * Delphi sets only the item there; the warehouse followed from the item's own lookup. Here the
     * Warehouse cell is read-only under option 1, so it is seeded too - otherwise the required-rule on
     * Warehouse could never be satisfied.
     */
    const handleInitNewRow = useCallback((e) => {
        e.data.headerRowId = headerRowId;

        if (!isDifferenceMode) return;

        e.data.quantityOrdered = props.differenceQuantity;

        if (itemLocked) {
            e.data.itemid = props.data?.itemid;
            e.data.itemCode = props.data?.itemCode;
            e.data.r_Warehouse = props.data?.r_Warehouse;
            e.data.warehouseIdentityId = props.data?.warehouseIdentityId;
        }
    }, [headerRowId, isDifferenceMode, itemLocked, props.differenceQuantity, props.data]);

    /**
     * Delphi opens this dialog with a row already being entered: the caller's `SetItemRowID` ends in
     * `grpActionSelectionClick(nil)`, and both Add branches leave the grid in `dsInsert` with the
     * defaults above and the cursor in a specific cell - Lot for option 1 (the item is fixed, only the
     * lot changes), Item for option 2 (`P_Item.Clear` then focus `edtP_Itemgrid`). Option 3 just
     * disables the grid. Clicking a different radio re-runs the whole thing.
     *
     * The web cannot reproduce Delphi's "find the row already staged for this item/lot and edit that
     * one instead" step - `ItemLotComboDetail` searches the `ProcessFlag='Y'` staging set, which does
     * not exist here (see OMD0022.md). What is reproduced is the part the user sees: a row open for
     * entry, pre-filled, focused on the right cell.
     */
    const appliedOption = useRef(null);

    const startRowForOption = useCallback(() => {
        const grid = dataGrid.current?.instance?.();
        if (!grid || !isDifferenceMode || !priority || !hasScope) return;

        // StateStoring's customLoad is an HTTP round-trip (GridLayout.sendStorageRequest), and
        // DevExtreme holds the data source back until the saved layout arrives. So on mount the widget
        // exists and `dataSource` is set, but the data controller has no store yet - and addRow() checks
        // exactly that, firing E1052 ("dxDataGrid should have the 'dataSource' option specified") into
        // the grid's error row, where it stays. Wait instead: onContentReady runs again once the state
        // has loaded. This has to come before appliedOption is stamped, or that retry is skipped.
        if (!grid.getDataSource()) return;

        // onContentReady fires on every data change; only react when the option actually changed.
        if (appliedOption.current === priority) return;
        appliedOption.current = priority;

        // Never cancelEditData() - in cell edit mode that re-saves the pending row. See dataStore.js.
        discardPendingEdits(grid);

        if (priority === DIFFERENCE_DO_NOTHING) return;

        // Lot for option 1 (the item is fixed, only the lot changes), Item for option 2.
        const focusColumn = itemLocked ? "lotCode" : "itemCode";

        Promise.resolve(grid.addRow())
            .then(() => {
                // Find the row by isNewRow rather than assuming index 0 - where a new row lands depends
                // on editing.newRowPosition, and guessing would open an editor on an existing line.
                const index = grid.getVisibleRows().findIndex((row) => row.isNewRow);
                if (index >= 0) grid.editCell(index, focusColumn);
            })
            .catch(() => { /* the grid was torn down mid-flight */ });
    }, [isDifferenceMode, priority, itemLocked, hasScope]);

    useEffect(() => { startRowForOption(); }, [startRowForOption]);

    const GetCodeByType = (type) => {
        const items = new DataSource({
            store: createStore({
                loadUrl: `${window._env_.REACT_APP_APIS}/${data.OM.GetCodeByType}`,
                key: 'rowid',
                loadParams: {
                    codeType: type
                },
                onBeforeSend: function (method, ajaxOptions) {
                    ajaxOptions.headers = {
                        Authorization: `Bearer ${accessToken}`
                    };
                }
            }),
            sort: [{ selector: "code", desc: false }],
            paginate: true,
            pageSize: 100
        });
        return items;
    };

    // Code 1-5 are Codes lookups filtered by CodeType 12-16 - the Tag values on edtCode1..edyCode5 in the
    // OMD0022 DFM. Rule 3: a dropdown, not free text.
    const renderCodeCell = (cellElement, codeType, currentValue) => {
        const items = GetCodeByType(codeType);
        return (
            <SelectBox
                dataSource={items}
                valueExpr="code"
                displayExpr="code"
                defaultValue={currentValue}
                acceptCustomValue={true}
                searchEnabled={true}
                searchExpr="Code"
                searchMode="contains"
                onValueChanged={async (e) => {
                    const selectedText = e.component.option('displayValue');
                    cellElement.setValue(selectedText);
                }}
            />
        );
    };

    const codeColumns = [
        { field: "userDefinedCode1", caption: "Code1", codeType: 12 },
        { field: "userDefinedCode2", caption: "Code2", codeType: 13 },
        { field: "userDefinedCode3", caption: "Code3", codeType: 14 },
        { field: "userDefinedCode4", caption: "Code4", codeType: 15 },
        { field: "userDefinedCode5", caption: "Code5", codeType: 16 },
    ];

    return (
        // The popup lays its content out as a flex column; this wrapper is the row that grows, and the
        // grid fills it. See .allocate-difference-popup__grid in om/css/omstyle.css.
        <div className="allocate-difference-popup__grid">
            <DataGrid
                height="100%"
                onExporting={(e) => {
                    if (window.handleDevExtremeDataGridExport) {
                        window.handleDevExtremeDataGridExport(e);
                    }
                }}
                ref={dataGrid}
                dataSource={detailDataSource}
                allowColumnReordering={true}
                allowColumnResizing={true}
                showBorders={true}
                columnAutoWidth={true}
                onInitNewRow={handleInitNewRow}
                onRowInserting={handleRowInserting}
                onRowInserted={handleRowInserted}
                onRowRemoved={handleRowRemoved}
                onRowUpdating={handleRowUpdating}
                onContentReady={startRowForOption}
            >
                <ColumnChooser
                    enabled={true}
                    title="Choose Column"
                    width={290}
                    height={290}
                    sortOrder="asc"
                >
                    <ColumnChooserSearch
                        enabled={true}
                        editorOptions={{ placeholder: 'Search column' }} />
                </ColumnChooser>
                <Export enabled={true} allowExportSelectedData={true} />

                <Editing
                    mode="cell"
                    // The load is pinned to the empty key and always comes back with no rows (see
                    // detailDataSource), so the grid's only content is what this session added, patched in
                    // from each insert's own response. DevExtreme's default refreshMode of "full" reloads
                    // the data source after every save, and that reload returned nothing - the row was
                    // saved, then immediately wiped off the grid. "reshape" keeps the returned row and
                    // re-applies sorting locally, with no extra GET.
                    refreshMode="reshape"
                    allowAdding={!editingDisabled && hasScope}
                    allowUpdating={!editingDisabled && hasScope}
                    allowDeleting={!editingDisabled && hasScope}
                    // Rule 12: project-standard delete-confirm wording.
                    texts={{ confirmDeleteMessage: "Delete selected record?" }}
                />
                <StateStoring
                    enabled={true}
                    type="custom"
                    customLoad={loadState}
                    storageKey={gridKey}
                    customSave={saveState}
                />
                <Column
                    dataField="type"
                    caption="Type"
                >
                    <Lookup
                        dataSource={recordTypes}
                        valueExpr="ID"
                        displayExpr="Text"
                    />
                </Column>
                {/* Rule 8: technical keys stay hidden. */}
                <Column dataField="detailRowId" caption='DetailRowId' visible={false} allowEditing={false} />
                <Column dataField="headerRowId" caption='HeaderRowId' visible={false} allowEditing={false} />
                <Column dataField="r_Warehouse" caption='r_Warehouse' visible={false} />
                <Column dataField="warehouseIdentityId" caption='Warehouse'
                    allowEditing={!itemLocked}
                    editCellRender={(cellElement) =>
                        RenderSelectBoxCell({
                            loadUrl: `${window._env_.REACT_APP_APIS}/${data.OM.GetWareHouses}`,
                            key: "rowid",
                            displayExpr: "code",
                            valueExpr: "rowid",
                            accessToken: accessToken,
                            defaultValue: cellElement.data.warehouseIdentityId,
                            onValueChangeCallback: (value, text) => {
                                cellElement.setValue(({ value, text }));
                            },
                        })
                    }
                    setCellValue={(rowData, newValue) => {
                        rowData.warehouseIdentityId = newValue.text;
                        rowData.r_Warehouse = newValue.value;
                    }}>
                    <RequiredRule message="The Warehouse field is required." /></Column>
                <Column dataField="itemid" caption='itemid' visible={false} />
                <Column dataField="itemCode" caption='Item'
                    allowEditing={!itemLocked}
                    editCellRender={(cellElement) => {
                        // The row's warehouse scopes the list when there is one; with none picked yet the
                        // parameter is left out altogether and the server returns every item. It has to be
                        // left out rather than sent empty: DevExtreme calls a loadParams function and
                        // encodes whatever it returns, so an undefined warehouse went over the wire as the
                        // literal "RWarehouse=undefined".
                        const rWarehouse = cellElement.data.r_Warehouse;

                        return RenderSelectBoxCell({
                            loadUrl: `${window._env_.REACT_APP_APIS}/${data.OM.GetItems}`,
                            key: "rowid",
                            displayExpr: "code",
                            valueExpr: "rowid",
                            accessToken: accessToken,
                            defaultValue: cellElement.data.itemCode,
                            loadParams: rWarehouse ? { RWarehouse: rWarehouse } : undefined,
                            onValueChangeCallback: (value, text) => {
                                cellElement.setValue(({ value, text }));
                            },
                        });
                    }}
                    setCellValue={(rowData, newValue) => {
                        rowData.itemCode = newValue.text;
                        rowData.itemid = newValue.value;
                    }}
                ></Column>
                <Column dataField="lotRowId" caption='LotRowId' visible={false} />
                <Column dataField="lotCode" caption='Lot' editCellRender={(cellElement) =>
                    RenderSelectBoxCell({
                        loadUrl: `${window._env_.REACT_APP_APIS}/${data.OM.GetLots}`,
                        key: "rowid",
                        displayExpr: "code",
                        valueExpr: "rowid",
                        accessToken: accessToken,
                        defaultValue: cellElement.data.lotCode,
                        loadParams: {
                            masterRowId: function () {
                                return cellElement.data.itemid;
                            }
                        },
                        onValueChangeCallback: (value, text) => {
                            cellElement.setValue(({ value, text }));
                        },
                    })
                }
                    setCellValue={(rowData, newValue) => {
                        rowData.lotCode = newValue.text;
                        rowData.lotRowId = newValue.value;
                    }}
                >
                </Column>
                <Column dataField="quantityOrdered" caption='Ordered' format={{
                    type: 'fixedPoint',
                    precision: 2
                }} />

                {/* The OMD0022 DFM sets this column's Title.Caption to "New Price" outright - it is not
                    mode-dependent, and in both modes the price being typed is the new line's. */}
                <Column dataField="unitPrice" caption='New Price' format={{
                    type: 'fixedPoint',
                    precision: 2
                }} />

                {codeColumns.map(({ field, caption, codeType }) => (
                    <Column
                        key={field}
                        dataField={field}
                        visible={false}
                        caption={caption}
                        editCellRender={(cellElement) => renderCodeCell(cellElement, codeType, cellElement.data[field])}
                    />
                ))}
                <Column dataField="userDefinedQuantity1" visible={false} caption='Quantity1' ></Column>
                <Column dataField="userDefinedQuantity2" visible={false} caption='Quantity2' ></Column>
                <Column dataField="userDefinedQuantity3" visible={false} caption='Quantity3' ></Column>
                <Column dataField="userDefinedQuantity4" visible={false} caption='Quantity4' ></Column>
                <Column dataField="userDefinedQuantity5" visible={false} caption='Quantity5' ></Column>
                <Column dataField="userDefinedDate1" dataType="date" format="MM/dd/yyyy" visible={false} caption='Date1' ></Column>
                <Column dataField="userDefinedDate2" dataType="date" format="MM/dd/yyyy" visible={false} caption='Date2' ></Column>
                <Column dataField="userDefinedDate3" dataType="date" format="MM/dd/yyyy" visible={false} caption='Date3' ></Column>
                <Column dataField="userDefinedDate4" dataType="date" format="MM/dd/yyyy" visible={false} caption='Date4' ></Column>
                <Column dataField="userDefinedDate5" dataType="date" format="MM/dd/yyyy" visible={false} caption='Date5' ></Column>
                <Column dataField="userDefinedReference1" visible={false} caption='Reference1' ></Column>
                <Column dataField="userDefinedReference2" visible={false} caption='Reference2' ></Column>
                <Column dataField="userDefinedReference3" visible={false} caption='Reference3' ></Column>
                <Column dataField="userDefinedReference4" visible={false} caption='Reference4' ></Column>
                <Column dataField="userDefinedReference5" visible={false} caption='Reference5' ></Column>
                <Toolbar>
                    <Item
                        disabled={editingDisabled || !hasScope}
                        name="addRowButton"
                    />
                    <Item location="after">
                        <GridLayoutManager gridKey={gridKey} dataGrid={dataGrid} setCurrentLayout={setCurrentLayout} />
                    </Item>
                    <Item name="exportButton" />
                    <Item name="columnChooserButton" />
                </Toolbar>
            </DataGrid>
        </div>
    )
};

export default SearchResultsForDialog;
